// Shared scenario types + EgoCrisis JSON story loader

export interface Character {
  id: string;
  name: string;
  role: string;
  color: string;
  avatar?: string;
}

export interface ChatMessage {
  id: string;
  sender: 'tutor' | 'user' | 'character';
  characterId?: string;
  text: string;
  timestamp?: string;
  type?: 'text' | 'single-choice' | 'multiple-choice' | 'free-text';
  options?: string[];
  correctAnswer?: string | string[];
  explanation?: string;
  /** Réaction du personnage À CHAQUE choix, indexée par le libellé de l'option (CCT-256).
   *  Un dialogue à embranchements se joue au choix du joueur : sans ça, l'app affichait la
   *  même explication globale pour tout le monde — donc la réfutation des options écartées
   *  à celui qui avait répondu juste. */
  optionResponses?: Record<string, string>;
  exploreAll?: boolean;
  /** A draw.io diagram carried BY THE TURN, shown in its bubble (CCT-477), and its zoom. */
  diagramUrl?: string;
  diagramZoom?: number;
}

export interface StoryContent {
  narrativeText: string;
  characters: string[];
}

export interface ChatContent {
  messages: ChatMessage[];
  tutorCharacterId: string;
}

export interface CardContent {
  title: string;
  body: string;
  /** A draw.io diagram shown full width inside the card (CCT-477), and its zoom. */
  diagramUrl?: string;
  diagramZoom?: number;
}

export interface QuizQuestion {
  id: string;
  question: string;
  type: 'single-choice' | 'multiple-choice' | 'free-text';
  options?: string[];
  correctAnswer?: string | string[];
  explanation: string;
}

export interface QuizContent {
  questions: QuizQuestion[];
}

export interface TaskStep {
  stepNumber: number;
  action: string;
  detail?: string;
}

export interface TaskContent {
  platform: string;
  objective: string;
  steps: TaskStep[];
  validation: string;
  azurePortalPath?: string;
}

export type SequenceItemContent = StoryContent | ChatContent | CardContent | QuizContent | TaskContent;

export interface SequenceItem {
  type: 'story' | 'chat' | 'card' | 'quiz' | 'task';
  id: string;
  title: string;
  content: SequenceItemContent;
  duration?: number;
  /** Server-side prerequisite gate; historical visits must not override it. */
  locked?: boolean;
  /** CCT-130 — musique propre à l'élément (prime sur celle du chapitre). */
  music?: string;
}

export interface ScenarioChapter {
  id: string;
  title: string;
  subtitle: string;
  learningObjectives: string[];
  sequence: SequenceItem[];
  /** CCT-130 — musique du chapitre (URL absolue servie par le runtime). */
  music?: string;
}

export interface EgoCrisisScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  coverImage: ReturnType<typeof require>;
  characters: Character[];
  chapters: ScenarioChapter[];
}

type JsonScenarioOverrides = Partial<{
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  characters: Character[];
  chapters: Array<Partial<ScenarioChapter> & { sequence?: Array<Partial<SequenceItem>> }>;
}>;

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asStringArray(value: unknown): string[] | null {
  return Array.isArray(value) && value.every(v => typeof v === 'string') ? value : null;
}

function mergeSequenceItem(base: SequenceItem, override: Partial<SequenceItem>): SequenceItem {
  const next: SequenceItem = { ...base };
  if (typeof override.id === 'string') next.id = override.id;
  if (typeof override.title === 'string') next.title = override.title;
  if (override.type && ['story', 'chat', 'card', 'quiz', 'task'].includes(override.type)) {
    next.type = override.type;
  }
  if (typeof override.duration === 'number') next.duration = override.duration;
  if (isObjectRecord(override.content)) {
    next.content = { ...(base.content as any), ...(override.content as any) } as SequenceItemContent;
  }
  // Sans cette ligne, la piste choisie sur l'élément était PERDUE à la fusion : l'app
  // retombait sur la musique bundlée sans rien signaler (CCT-135).
  if (typeof (override as any).music === 'string') (next as any).music = (override as any).music;
  return next;
}

function mergeChapter(base: ScenarioChapter, override: Partial<ScenarioChapter> & { sequence?: Array<Partial<SequenceItem>> }): ScenarioChapter {
  const next: ScenarioChapter = { ...base };
  if (typeof override.id === 'string') next.id = override.id;
  if (typeof override.title === 'string') next.title = override.title;
  if (typeof override.subtitle === 'string') next.subtitle = override.subtitle;
  const learningObjectives = asStringArray(override.learningObjectives);
  if (learningObjectives) next.learningObjectives = learningObjectives;
  if (typeof (override as any).music === 'string') (next as any).music = (override as any).music;
  if (Array.isArray(override.sequence)) {
    next.sequence = base.sequence.map((item, index) => mergeSequenceItem(item, override.sequence?.[index] ?? {}));
    if (override.sequence.length > base.sequence.length) {
      const extra = override.sequence.slice(base.sequence.length).filter(isObjectRecord) as Array<Partial<SequenceItem>>;
      for (const candidate of extra) {
        if (
          typeof candidate.id === 'string'
          && typeof candidate.title === 'string'
          && typeof candidate.type === 'string'
          && ['story', 'chat', 'card', 'quiz', 'task'].includes(candidate.type)
          && isObjectRecord(candidate.content)
        ) {
          next.sequence.push(candidate as SequenceItem);
        }
      }
    }
  }
  return next;
}

function extractScenarioOverrides(payload: unknown): JsonScenarioOverrides | null {
  const raw = isObjectRecord(payload) && isObjectRecord((payload as Record<string, unknown>).default)
    ? (payload as Record<string, unknown>).default
    : payload;
  if (!isObjectRecord(raw)) return null;
  if (isObjectRecord(raw.scenario)) {
    return raw.scenario as JsonScenarioOverrides;
  }
  return raw as JsonScenarioOverrides;
}

export function loadScenarioFromJsonPayload(
  staticMeta: Pick<EgoCrisisScenario, 'id' | 'title' | 'subtitle' | 'certification' | 'coverImage'>,
  payload: unknown,
): EgoCrisisScenario {
  const base: EgoCrisisScenario = { ...staticMeta, characters: [], chapters: [] };
  return mergeScenarioWithJson(base, extractScenarioOverrides(payload));
}

function mergeScenarioWithJson(base: EgoCrisisScenario, overrides: JsonScenarioOverrides | null): EgoCrisisScenario {
  if (!overrides) return base;
  const next: EgoCrisisScenario = {
    ...base,
    id: typeof overrides.id === 'string' ? overrides.id : base.id,
    title: typeof overrides.title === 'string' ? overrides.title : base.title,
    subtitle: typeof overrides.subtitle === 'string' ? overrides.subtitle : base.subtitle,
    certification: typeof overrides.certification === 'string' ? overrides.certification : base.certification,
    characters: Array.isArray(overrides.characters) && overrides.characters.length > 0 ? overrides.characters : base.characters,
    chapters: base.chapters,
  };
  if (Array.isArray(overrides.chapters)) {
    next.chapters = base.chapters.map((chapter, idx) => mergeChapter(chapter, overrides.chapters?.[idx] ?? {}));
    if (overrides.chapters.length > base.chapters.length) {
      const extra = overrides.chapters.slice(base.chapters.length).filter(isObjectRecord) as Array<Partial<ScenarioChapter> & { sequence?: Array<Partial<SequenceItem>> }>;
      for (const chapter of extra) {
        if (
          typeof chapter.id === 'string'
          && typeof chapter.title === 'string'
          && typeof chapter.subtitle === 'string'
          && Array.isArray(chapter.learningObjectives)
          && Array.isArray(chapter.sequence)
        ) {
          next.chapters.push(chapter as ScenarioChapter);
        }
      }
    }
  }
  return next;
}

